/**
 * @module billingManager
 */
import { services } from "../../constants";
import { toUserCache } from "./user/userCacheDecorators";
import { geolocation, token, phoneVerificationData } from "../store";
import errors from "../errors";
import { setCookie } from "../../utils/cookiesUtils";
import {
  fromMopsCache,
  toMopsCache,
  toPaymentMethodCache,
  fromPaymentMethodCache,
  toSubscriptionsCache,
  fromSubscriptionsCache
} from "./billingCacheDecorators";

function getCountry(locationCountry) {
  const cachedCountry = geolocation.get() ? geolocation.get().country : "";
  return locationCountry ? locationCountry.toLowerCase() : cachedCountry.toLowerCase();
}

function createDictionalry(dataList) {
  let dictionary = {};
  for (var key in dataList) {
    for (var innerKey in dataList[key].paymentPlans) {
      var element = dataList[key].paymentPlans[innerKey].id;
      if (element) {
        dictionary[element] = dataList[key];
      }
    }
  }
  return dictionary;
}

/**
 * Depending on the data coming from the app, gets the proper headers to be sent to the request, meaning
 * that if the user is trying to activate a payment through an operator (which will bring an 'otpCode' field
 * along with the data coming from the app), apart from the Authorization header containing the user token,
 * it will also demand to include the phone verification code that has been stored after verifying its phone.
 * @param {String|undefined} otpCode - Code sent to user through SMS during an operator payment activation process.
 * @param {Object} additionalParameters - Additional parameters to be used in headers eg. campaignName for discount.
 * @returns {Object} Object containing all the headers to be sent in the request.
 * @private
 */
async function getActivationHeaders(otpCode, additionalParameters) {
  const requestHeaders = await this.getHeaders.call(this);

  if (otpCode !== undefined) {
    const phoneVerificationCode = await phoneVerificationData.get.call(this);
    if (phoneVerificationCode !== null) {
      Object.assign(requestHeaders.headers, { phoneVerificationCode });
    } else {
      throw Error(errors.billing.MISSING_MANDATORY_PHONE_VERIFICATION_CODE);
    }
  }
  if (additionalParameters !== undefined) {
    if (additionalParameters.campaignName !== undefined) {
      // Object.assign(requestHeaders.headers, { campaignName: additionalParameters.campaignName });
    }
  }

  return requestHeaders;
}

/**
 * Returns information about user prommotions
 * @public
 */

async function getUserPromotions(payload) {
  try {
    let query = "";
    query = payload && payload.list && this.parseQueryString({ list: payload.list });
    const url = `${this.buildUrl("settings", services.user.promotions)}${!!query ? `?${query}` : ""}`;
    const response = await this.http.get(url, await this.getHeaders.call(this));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Sends a request to backend to activate/register a user's payment method. If user account is not active and there
 * is a new token retrieved from the request, user token will be updated in SDK context.
 * @param {Object} activationConfig - Payment data object to be activated.
 * @param {String} activationConfig.paymentType - Payment method type such as 'voucher' or 'creditCard'.
 * @param {String} activationConfig.otpCode - Code sent to user through SMS during an operator payment activation process.
 * @param {Object} activationConfig.paymentData - Extra information on the payment method provided.
 * @param {String} activationConfig.paymentData.voucherNumber - Voucher number. Only for 'voucher' payment type.
 * @param {String} activationConfig.paymentData.cardNumber - Card number. Only for 'creditCard' payment type.
 * @param {String} activationConfig.paymentData.cardType - Card type such as 'visa' or 'mastercard'. Only for 'creditCard' payment type. Its value
 * will be turn into lower case if received.
 * @param {String} activationConfig.paymentData.name - Card holder name. Only for 'creditCard' payment type.
 * @param {String} activationConfig.paymentData.surname - Card holder surname. Only for 'creditCard' payment type.
 * @param {String} activationConfig.paymentData.expirationDate - Card expiration date. Only for 'creditCard' payment type.
 * @param {String} activationConfig.paymentData.cvv - CVV card security code. Only for 'creditCard' payment type. Format should match 'MM/YYYY'
 * @param {Object} additionalParameters - Additional parameters eg. campaignName.for discount
 * @public
 */
async function activate(activationConfig, additionalParameters) {
  try {
    const headers = await getActivationHeaders.call(this, activationConfig.otpCode, additionalParameters);
    if (additionalParameters !== undefined) {
      if (additionalParameters.campaignName !== undefined) {
        Object.assign(activationConfig, { campaignName: additionalParameters.campaignName });
      }
    }
    const response = await this.http.post(
      this.buildUrl("settings", services.billing.paymentActivation),
      activationConfig,
      headers
    );
    if (response.data.token) {
      const jwtCookie = this.jwtCookie;
      token.updateToken.call(this, response.data.token);
      setCookie(jwtCookie.name, response.data.token, jwtCookie.expirationTime);
    }
    const userData = {
      ...response.data.user,
      ...(response.data.paymentResponse &&
        response.data.paymentResponse.discountDuration && {
          discountDuration: response.data.paymentResponse.discountDuration
        }),
      ...(response.data.paymentResponse &&
        response.data.paymentResponse.subscriptions && {
          subscriptions: response.data.paymentResponse.subscriptions
        })
    };
    return userData;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

 async function payfortThreeDSAuthorization(data) {
  const {paymentName} = data;
  const url = `${this.buildUrl("settings", services.billing.payfortAuthorization)}?paymentName=${paymentName}`;
  try {
    const response = await this.http.post(url, data, await this.getHeaders());
    return response;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}


/**
 * Returns information about tvod activated asset
 * @param {Object} data
 * @public
 */

async function activateTvod(data = {}) {
  try {
    // const timeout = this.networkRequestTimeouts.activateChannel;
    const response = await this.http.post(this.buildUrl("settings", services.billing.tvodActivation), data, {
      // ...(timeout !== undefined && { timeout }),
      ...(await this.getHeaders.call(this))
    });
    return response.data;
  } catch (error) {
    console.log("erorr activate tvod service", error);
    this.handleErrorResponse(error);
  }
}

/**
 * Returns information about available payments
 * @param {String} Country
 * @public
 */
async function getPaymentPlans(locationCountry, type, opts = { includeCountry: true }) {
  try {
    let params = "";

    if (opts.includeCountry) {
      params += `country=${getCountry(locationCountry)}`;
    }

    if (type) {
      params += `${params.length ? "&" : ""}type=${type}`;
    }

    const url = `${this.buildUrl("payments", services.billing.paymentPlans)}?${params}`;
    const response = await this.http.get(url, await this.getHeaders.call(this));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns information about activation link
 * @param {Object} paymentData
 * @param {String} paymentData.uniqueId
 * @param {String} paymentData.operator
 * @param {String} paymentData.paymentPlanId
 * @public
 */
async function paymentActivationLink(paymentData) {
  try {
    const url = `${this.buildUrl("settings", services.billing.paymentActivationLink)}`;
    const response = await this.http.post(url, paymentData, await this.getHeaders.call(this));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns payfort credit card data
 * @param {Object} cardData Data of the credit card
 * @param {String} paymentName
 * @public
 */
async function getPayfortCreditCardData(cardData, paymentName) {
  try {
    const url = `${this.buildUrl("settings", services.billing.payfortCreditCard)}?paymentName=${paymentName}`;
    const response = await this.http.post(url, { cardData }, await this.getHeaders());
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns payfort credit card data
 * @param {String} paymentName
 * @public
 */
async function getAdyenPaymentMethodsData(paymentName) {
  try {
    const url = `${this.buildUrl("settings", services.billing.adyenCreditCard)}`;
    const response = await this.http.post(url, { paymentName }, await this.getHeaders());
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns adyen credit card tokenization data
 * @param {Object} state
 * @public
 */
async function getAdyenTokenizationData(cardInfo) {
  try {
    const url = `${this.buildUrl("settings", services.billing.adyenCreditCardTokenization)}`;
    const response = await this.http.post(url, cardInfo, await this.getHeaders());
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns adyen credit card tokenization data
 * @param {Object} state
 * @public
 */
async function getPaypalSetupToken(payload) {
  try {
    const url = `${this.buildUrl("settings", services.billing.paypalSetupToken)}`;
    const response = await this.http.post(url, payload, await this.getHeaders());
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns adyen credit card tokenization data
 * @param {Object} state
 * @public
 */
async function getPaypalPaymentToken(payload) {
  try {
    const url = `${this.buildUrl("settings", services.billing.paypalPaymentToken)}`;
    const response = await this.http.post(url, payload, await this.getHeaders());
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns adyen credit card tokenization data after additional verification like 3D secure
 * @param {Object} state
 * @public
 */
async function getAdyenAdditionalTokenizationData(cardInfo) {
  try {
    const url = `${this.buildUrl("settings", services.billing.adyenCreditCardAdditionalVerification)}`;
    const response = await this.http.post(url, cardInfo, await this.getHeaders());
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns adyen tokenization data after verifying redirect result response
 * @param {Object} state
 * @public
 */
async function getAdyenPaymentDetailsVerificationData(details) {
  try {
    const url = `${this.buildUrl("settings", services.billing.adyenCreditCardPaymentDetailsVerification)}`;
    const response = await this.http.post(url, details, await this.getHeaders());
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns information about available subscriptions
 * @param {String} Country
 * @param {String} subscriptionName Name of the subscription
 * @public
 */
async function getSubscriptions({
  country,
  subscription = null,
  mopName = null,
  planId = null,
  enforcedStatic = false
}) {
  try {
    const extraParamsQuery = this.parseQueryString({ subscription, mopName, planId });
    const countryQuery = !!country ? `country=${getCountry(country)}` : "";
    const queries = [countryQuery, extraParamsQuery].filter(queryGroup => !!queryGroup).join("&");
    let url = "";
    if (enforcedStatic) {
      // This is the url for cdn subscription call, we are using this for now
      url = `${this.backendHosts.cdn.payments}${services.billing.subscriptionsCDN}${!!queries ? `?${queries}` : ""}`;
    } else {
      // This is the url for the subscription call
      url = `${this.buildUrl("payments", services.billing.subscriptions)}${!!queries ? `?${queries}` : ""}`;
    }
    const response = await this.http.get(url, await this.getHeaders.call(this));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns information about available subscriptions
 * @param {String} Country
 * @param {String} subscriptionName Name of the subscription
 * @public
 */
async function getSubscriptionsFromCdn({
  country,
  subscription = null,
  mopName = null,
  planId = null,
  enforcedStatic = true
}) {
  try {
    const extraParamsQuery = this.parseQueryString({ subscription, mopName, planId });
    const countryQuery = !!country ? `country=${getCountry(country)}` : "";
    const queries = [countryQuery, extraParamsQuery].filter(queryGroup => !!queryGroup).join("&");
    const url = `${this.backendHosts.cdn.payments}${services.billing.subscriptionsCDN}${
      !!queries ? `?${queries}` : ""
    }`;
    const response = await this.http.get(url, await this.getHeaders.call(this));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns configuration for all mops data
 * @public
 */

async function getAllMops() {
  try {
    const url = this.buildUrl("staticSpxV1", `${services.allMops.general}`);
    const response = await this.http.get(url);
    const dictionary = await createDictionalry(response.data.paymentMethods);
    return dictionary;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns configuration for all mops data for big screens
 * @public
 */

async function getAllMopsForBigScreens() {
  try {
    const url = this.buildUrl("settings", `${services.allMops.fromMiddleware}`);
    const response = await this.http.get(url);
    return response.data;
    // test
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

async function generateEasypaisaOTP(payload) {
  try {
    const url = this.buildUrl("settings", `${services.easypaisa.generateEasypaisaOTP}`);
    const response = await this.http.post(url, payload, await this.getHeaders.call(this));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns configuration for specific method of payment data
 * @public
 */

async function getPaymentMethod(planId) {
  try {
    const url = this.buildUrl("settings", `${services.paymentMethod.general}?planId=${planId}`);
    const response = await this.http.get(url);
    const objResponse = {
      [planId]: response.data.paymentMethods[0]
    };
    return objResponse;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns configuration for specific method of payment data
 * @public
 */

 async function getPaymentMethodById(planId) {
  try {
    const url = this.buildUrl("settings", `${services.paymentMethod.general}?planId=${planId}`);
    const response = await this.http.get(url);
    const objResponse = {
      [planId]: response.data.paymentMethods[0]
    };
    return objResponse;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns billing info data of user subscriptions
 * @param {Object} state
 * @private
 */
async function getBillingCardInfoData(payload) {
  try {
    const url = `${this.buildUrl("settings", `${services.billing.latestCardInfo}?country=${payload.country}`)}`;
    const response = await this.http.get(url, await this.getHeaders());
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Billing related methods for the SDK. This manager can be used to activate new user's payment methods,
 * to allow changing user's account status and be able to navigate throughout content without restriction,
 * as well as to manage them.
 */
export default {
  activate: toUserCache(activate),
  activateTvod,
  getPaymentPlans,
  getPayfortCreditCardData,
  // getSubscriptions,
  getSubscriptions: toSubscriptionsCache(fromSubscriptionsCache(getSubscriptions)),
  getAdyenPaymentMethodsData,
  getAdyenTokenizationData,
  getPaypalSetupToken,
  getPaypalPaymentToken,
  payfortThreeDSAuthorization,
  getAdyenAdditionalTokenizationData,
  paymentActivationLink,
  getAllMops: toMopsCache(fromMopsCache(getAllMops)),
  getAllMopsForBigScreens: toMopsCache(fromMopsCache(getAllMopsForBigScreens)),
  getUserPromotions,
  getAdyenPaymentDetailsVerificationData,
  getSubscriptionsFromCdn,
  // getPaymentMethod: toPaymentMethodCache(fromPaymentMethodCache(getPaymentMethod)),
  getPaymentMethod: getPaymentMethod,
  getPaymentMethodById: toPaymentMethodCache(fromPaymentMethodCache(getPaymentMethodById)),
  generateEasypaisaOTP,
  // getSubscriptionsFromCdn,
  getBillingCardInfoData
};
